import { i18n } from "@tellescope/i18n";
import { Button, cn, NumberInput, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@tellescope/ui";
import type {
  CalendarEventReminder,
  CalendarEventReminderInfoForType,
  CalendarEventReminderType,
} from "@tellescope/types-models";
import { discriminatedStringUnionIncludes } from "@tellescope/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Settings2, Trash2 } from "lucide-react";
import { NotificationInfoSection } from "./NotificationInfoSection";
import { JourneyInfoSection } from "./JourneyInfoSection";
import { WebhookInfoSection } from "./WebhookInfoSection";
import { CreateTicketInfoSection } from "./CreateTicketInfoSection";

const getReminderTypeLabelMap = (): Record<CalendarEventReminder["type"], string> =>
  ({
    webhook: i18n.t("Webhook", { ns: "calendar" }),
    "add-to-journey": i18n.t("Add to Journey", { ns: "calendar" }),
    "create-ticket": i18n.t("Create Ticket", { ns: "calendar" }),
    "user-notification": i18n.t("Team Notification", { ns: "calendar" }),
    "Remove From Journey": i18n.t("Remove from Journey", { ns: "calendar" }),
    "enduser-notification": i18n.t("Patient Notification", { ns: "calendar" }),
  }) as const;

const convertStringToReminderType = (str: string): CalendarEventReminder["type"] | undefined =>
  Object.keys(getReminderTypeLabelMap()).find((key) => key === str) as CalendarEventReminder["type"] | undefined;
const SHARED_REMINDER_KEYS = [
  "type",
  "msBeforeStartTime",
  "dontSendIfPassed",
  "didRemind",
  "dontSendIfJoined",
  "skipEnduserIds",
] as const;
type SharedReminderKey = (typeof SHARED_REMINDER_KEYS)[number];

const addInfoToReminder = <T extends CalendarEventReminderType>(
  reminder: Omit<CalendarEventReminder, "info">,
  info: CalendarEventReminderInfoForType[T]["info"]
): CalendarEventReminderInfoForType[T] =>
  ({
    ...reminder,
    info,
  }) as CalendarEventReminderInfoForType[T];

const convertReminderTypeToOtherType = <T extends CalendarEventReminderType>(
  reminder: CalendarEventReminder,
  type: T,
  newInfo?: CalendarEventReminderInfoForType[T]["info"]
): CalendarEventReminderInfoForType[T] => {
  const sharedData = Object.fromEntries(
    Object.entries(reminder).filter(([key]) => discriminatedStringUnionIncludes(SHARED_REMINDER_KEYS, key))
  ) as Pick<CalendarEventReminder, SharedReminderKey>;
  const updatedObj = {
    ...sharedData,
    type: type as T,
  };

  switch (reminder.type) {
    case "create-ticket":
      return addInfoToReminder<"create-ticket">(
        updatedObj,
        (newInfo as CalendarEventReminderInfoForType["create-ticket"]["info"]) ?? {
          title: "",
        }
      ) as CalendarEventReminderInfoForType[T];
    case "enduser-notification":
      return addInfoToReminder<"enduser-notification">(
        updatedObj,
        newInfo ?? {}
      ) as CalendarEventReminderInfoForType[T];
    case "user-notification":
      return addInfoToReminder<"user-notification">(updatedObj, newInfo ?? {}) as CalendarEventReminderInfoForType[T];
    case "Remove From Journey":
      return addInfoToReminder<"Remove From Journey">(
        updatedObj,
        (newInfo as CalendarEventReminderInfoForType["Remove From Journey"]["info"]) ?? {
          journeyId: "",
        }
      ) as CalendarEventReminderInfoForType[T];
    case "add-to-journey":
      return addInfoToReminder<"add-to-journey">(
        updatedObj,
        (newInfo as CalendarEventReminderInfoForType["add-to-journey"]["info"]) ?? {
          journeyId: "",
        }
      ) as CalendarEventReminderInfoForType[T];
    case "webhook":
      return addInfoToReminder<"webhook">(updatedObj, newInfo ?? {}) as CalendarEventReminderInfoForType[T];
  }
};

const MS_PER_UNIT = {
  mins: 1000 * 60,
  hours: 1000 * 60 * 60,
  days: 1000 * 60 * 60 * 24,
} as const;
type TimeFactor = keyof typeof MS_PER_UNIT;
const getTimeFactorLabels = (): Record<TimeFactor, string> => ({
  mins: i18n.t("Minutes"),
  hours: i18n.t("Hours"),
  days: i18n.t("Days"),
});

const getInitialTimeFactorData = (ms: number): { type: TimeFactor; remainder: number } => {
  const absMs = Math.abs(ms);
  const remainders = (
    [
      { type: "days", remainder: absMs % MS_PER_UNIT.days },
      { type: "hours", remainder: absMs % MS_PER_UNIT.hours },
      { type: "mins", remainder: absMs % MS_PER_UNIT.mins },
    ] satisfies { type: TimeFactor; remainder: number }[]
  ).sort((a, b) => a.remainder - b.remainder);
  return remainders[0]!;
};

const convertStringToTimeFactor = (str: string): TimeFactor | undefined => {
  return Object.keys(MS_PER_UNIT).find((val) => val === str) as TimeFactor | undefined;
};

export interface ReminderInputProps {
  value: CalendarEventReminder;
  onChange: (newValue: CalendarEventReminder) => void;
  onDelete: () => void;
}

export const ReminderInput = ({ value, onChange, onDelete }: ReminderInputProps) => {
  const { t } = useTranslation("calendar");
  const [numberInputValue, setNumberInputValue] = useState(() => {
    const absMs = Math.abs(value.msBeforeStartTime);
    const unit = getInitialTimeFactorData(value.msBeforeStartTime).type;
    return Math.round(absMs / MS_PER_UNIT[unit]) * (value.msBeforeStartTime < 0 ? -1 : 1);
  });
  const [selectedTimeFactor, setSelectedTimeFactor] = useState<TimeFactor>(
    getInitialTimeFactorData(value.msBeforeStartTime).type
  );
  const [beforeOrAfter, setBeforeOrAfter] = useState(value.msBeforeStartTime < 0 ? "after" : "before");
  const [showInfoSection, setShowInfoSection] = useState(false);

  return (
    <div className={cn("p-3 rounded-md border flex flex-col gap-0 transition-all", showInfoSection && "gap-4")}>
      <div className="flex gap-2 items-center">
        <Select
          value={value.type}
          onValueChange={(newVal) => {
            const newType = convertStringToReminderType(newVal);
            if (newType === undefined) return;
            const notificationTypes = ["user-notification", "enduser-notification"];
            const wereBothNotifications = notificationTypes.includes(value.type) && notificationTypes.includes(newVal);
            const journeyTypes = ["add-to-journey", "Remove From Journey"];
            const shouldShareJourneyId = journeyTypes.includes(value.type) && journeyTypes.includes(newVal);
            onChange(
              convertReminderTypeToOtherType(
                value,
                newType,
                wereBothNotifications
                  ? value.info
                  : shouldShareJourneyId
                  ? { journeyId: "journeyId" in value.info ? value.info.journeyId : "" }
                  : undefined
              )
            );
          }}
        >
          <SelectTrigger className="grow text-xs px-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(getReminderTypeLabelMap()).map(([value, label]) => (
              <SelectItem key={value} value={value}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <NumberInput
          value={Math.abs(numberInputValue)}
          onChange={(newVal) => {
            const nextBeforeOrAfter = newVal < 0 ? "after" : beforeOrAfter;
            const absVal = Math.abs(newVal);
            setBeforeOrAfter(nextBeforeOrAfter);
            setNumberInputValue(absVal * (nextBeforeOrAfter === "after" ? -1 : 1));
            const ms = absVal * MS_PER_UNIT[selectedTimeFactor];
            onChange({
              ...value,
              msBeforeStartTime: nextBeforeOrAfter === "after" ? -ms : ms,
            });
          }}
          className="grow px-2 w-auto"
        />
        <Select
          value={selectedTimeFactor}
          onValueChange={(str) => {
            const timeFactor = convertStringToTimeFactor(str);
            if (!timeFactor) return;

            setSelectedTimeFactor(timeFactor);
            const absVal = Math.abs(numberInputValue);
            const ms = absVal * MS_PER_UNIT[timeFactor];
            onChange({
              ...value,
              msBeforeStartTime: beforeOrAfter === "after" ? -ms : ms,
            });
          }}
        >
          <SelectTrigger className="grow text-xs px-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.keys(MS_PER_UNIT).map((factor) => (
              <SelectItem key={factor} value={factor}>
                {getTimeFactorLabels()[factor as TimeFactor] ?? ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={beforeOrAfter}
          onValueChange={(newVal) => {
            setBeforeOrAfter(newVal);
            if (newVal === "after") {
              onChange({
                ...value,
                msBeforeStartTime: Math.abs(value.msBeforeStartTime) * -1,
              });
            } else {
              onChange({
                ...value,
                msBeforeStartTime: Math.abs(value.msBeforeStartTime),
              });
            }
          }}
        >
          <SelectTrigger className="grow text-xs px-2">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="before">{t("before")}</SelectItem>
            <SelectItem value="after">{t("after")}</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-0 shrink">
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              setShowInfoSection((prev) => !prev);
            }}
            className="flex-shrink p-1"
          >
            <Settings2 className="size-3.5" />
            <ChevronDown className={cn("size-3 flex-shrink transition-all", showInfoSection && "rotate-180")} />
          </Button>
          <Button
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="hover:text-destructive p-1 size-6"
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>
      {showInfoSection && (
        <div className={cn("overflow-hidden flex flex-col transition-all space-y-4 flex-grow")}>
          <div className="w-full h-[1px] bg-gray-200" />
          {value.type === "enduser-notification" || value.type === "user-notification" ? (
            <NotificationInfoSection
              value={value}
              onChange={onChange}
              mode={value.type === "enduser-notification" ? "enduser" : "team"}
            />
          ) : value.type === "add-to-journey" ? (
            <JourneyInfoSection value={value} onChange={onChange} mode="add" />
          ) : value.type === "Remove From Journey" ? (
            <JourneyInfoSection value={value} onChange={onChange} mode="remove" />
          ) : value.type === "webhook" ? (
            <WebhookInfoSection value={value} onChange={onChange} />
          ) : value.type === "create-ticket" ? (
            <CreateTicketInfoSection value={value} onChange={onChange} />
          ) : null}
        </div>
      )}
    </div>
  );
};
